import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AlertTriangle } from "lucide-react";

// Same center/zoom as the public /liveverkehr page
const QUARTIER_CENTER = [51.9325, 8.8810];
const INITIAL_ZOOM = 15;
const MIN_ZOOM = 12;
const MAX_ZOOM = 19;
const DETMOLD_BOUNDS = [
  [51.88, 8.78],
  [52.00, 8.97],
];
const TOMTOM_TILE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/tomtom/traffic-tile/relative-delay/{z}/{x}/{y}.png?thickness=8`;

/**
 * MapSlide — renders the live traffic map inside a screen slide.
 * Used in the Public-Display screen rotation (after every 5 news).
 */
export default function MapSlide({ sperrungen = [], dataTestId = "map-slide" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: QUARTIER_CENTER,
      zoom: INITIAL_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: DETMOLD_BOUNDS,
      maxBoundsViscosity: 0.9,
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: MAX_ZOOM,
    }).addTo(map);

    // Traffic layer via backend proxy (15-min cache, no key in browser).
    L.tileLayer(TOMTOM_TILE_URL, { maxZoom: MAX_ZOOM, opacity: 0.9 }).addTo(map);

    // Quartier pin in centre
    const pin = L.divIcon({
      className: "detmold-pin",
      html: `<span></span>`,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
    L.marker(QUARTIER_CENTER, { icon: pin }).addTo(map);

    // Sperrungs-markers
    sperrungen.forEach((s) => {
      if (typeof s.lat !== "number" || typeof s.lng !== "number") return;
      const sev = s.severity || "closure";
      const colorClass = sev === "warning" ? "warning" : sev === "detour" ? "detour" : "closure";
      const icon = L.divIcon({
        className: `sperrung-marker sperrung-${colorClass}`,
        html: `
          <span class="sperrung-pulse"></span>
          <span class="sperrung-core">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </span>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
      L.marker([s.lat, s.lng], { icon }).addTo(map);
    });

    // Ensure proper sizing after mount
    setTimeout(() => map.invalidateSize(), 60);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="absolute inset-0 flex flex-col px-10 py-6 screen-fade-in" data-testid={dataTestId}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-[12px] uppercase tracking-[0.2em] text-[hsl(var(--brand-green-strong))] font-medium inline-flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" /> Detmold — LIVE-Verkehr
          </p>
          <h2 className="font-semibold leading-tight tracking-tight mt-1" style={{ fontSize: "clamp(1.6rem, 2.4vw, 2.6rem)" }}>
            Verkehrslage Innenstadt
          </h2>
        </div>
        <div className="flex items-center gap-4 text-xs text-neutral-600">
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 bg-[#22C55E] rounded-sm" /> Frei</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 bg-[#F59E0B] rounded-sm" /> Dicht</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 bg-[#EF4444] rounded-sm" /> Stau</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#DC2626] text-white">
              <AlertTriangle className="h-2.5 w-2.5" />
            </span> Sperrung
          </span>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 w-full border border-brand-line shadow-[0_24px_60px_-30px_rgba(0,0,0,0.30)] rounded-sm overflow-hidden" />
    </div>
  );
}
